/**
 * PÁGINA TEMPORAL DE UTILIDAD — borrar cuando no se necesite más.
 * Ruta: /admin/reset-deposito
 *
 * ELIMINA los pedidos de un depósito que todavía NO fueron controlados, es decir
 * los que están en etapas PREVIAS a "Pendiente de Despachar"
 * (PENDIENTE_ASIGNAR, ASIGNADO, EN_PREPARACION, CON_ERROR, ERROR_CONFIRMADO,
 * PREPARADO, y cualquier otro que no sea CONTROLADO).
 *
 * CONSERVA:
 *  - los pedidos CONTROLADO (ya se enviaron a despachar / se imprimieron etiquetas),
 *  - la colección `pedidos_despachados` (NO se toca),
 *  - la colección `pedidos_entregados` (NO se toca).
 *
 * Solo admin/ventas (lo exige la regla de delete de `pedidos`).
 */

import { useState } from "react";
import { collection, query, where, getDocs, writeBatch, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

const DEPOSITOS = ["ISABELA", "R8"];
// Estado que marca "Pendiente de Despachar". Desde acá en adelante NO se elimina.
const ESTADO_CONSERVADO = "CONTROLADO";

export default function AdminResetDeposito() {
  const { user } = useAuth();
  const [deposito, setDeposito]       = useState("R8");
  const [pedidos, setPedidos]         = useState(null);   // pedidos del depósito
  const [despachados, setDespachados] = useState(null);   // solo para informar (no se tocan)
  const [loading, setLoading]         = useState(false);
  const [borrando, setBorrando]       = useState(false);
  const [resultado, setResultado]     = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  async function cargarPedidos() {
    setLoading(true);
    setResultado(null);
    setPedidos(null);
    setDespachados(null);
    try {
      const [snapPedidos, snapDesp] = await Promise.all([
        getDocs(query(collection(db, "pedidos"), where("deposito", "==", deposito))),
        getDocs(query(collection(db, "pedidos_despachados"), where("deposito", "==", deposito))),
      ]);
      setPedidos(snapPedidos.docs.map(d => ({ id: d.id, ...d.data() })));
      setDespachados(snapDesp.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      alert("Error al cargar: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function eliminar() {
    if (!pedidos) return;
    setBorrando(true);
    setResultado(null);
    try {
      let countDeleted = 0;
      const chunks = [];
      for (let i = 0; i < aEliminar.length; i += 499) chunks.push(aEliminar.slice(i, i + 499));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        for (const p of chunk) { batch.delete(doc(db, "pedidos", p.id)); countDeleted++; }
        await batch.commit();
      }
      setResultado({ ok: true, countDeleted, countKept: conservados.length });
      setPedidos(null);
      setDespachados(null);
      setConfirmando(false);
    } catch (e) {
      setResultado({ ok: false, error: e.message });
    } finally {
      setBorrando(false);
    }
  }

  // Partición: lo que se elimina (no controlado) vs lo que se conserva (controlado).
  const aEliminar   = pedidos ? pedidos.filter(p => p.estado !== ESTADO_CONSERVADO) : [];
  const conservados = pedidos ? pedidos.filter(p => p.estado === ESTADO_CONSERVADO) : [];

  // Resumen por estado de lo que se ELIMINARÁ.
  const resumenEstados = pedidos
    ? aEliminar.reduce((acc, p) => {
        const est = p.estado || "sin estado";
        acc[est] = (acc[est] || 0) + 1;
        return acc;
      }, {})
    : null;

  const totalDespachados = despachados ? despachados.length : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px" }}>
      <div style={{ width: "100%", maxWidth: "560px" }}>

        {/* Cabecera */}
        <div style={{ background: "#fef9c3", border: "1.5px solid #fde047", borderRadius: "14px", padding: "16px 20px", marginBottom: "24px" }}>
          <p style={{ margin: "0 0 4px", fontSize: "0.75rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            🛠️ Utilidad temporal
          </p>
          <p style={{ margin: 0, fontSize: "0.88rem", color: "#78350f" }}>
            <strong>Elimina</strong> los pedidos que todavía NO fueron controlados (etapas previas a <strong>Pendiente de Despachar</strong>).
            Los <strong>CONTROLADO</strong> (ya enviados a despachar / con etiquetas impresas), los <strong>despachados</strong> y los <strong>entregados</strong> NO se tocan.
          </p>
        </div>

        {/* Selector de depósito */}
        <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", marginBottom: "16px" }}>
          <p style={{ margin: "0 0 10px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Depósito
          </p>
          <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
            {DEPOSITOS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => { setDeposito(d); setPedidos(null); setResultado(null); setConfirmando(false); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: "10px",
                  border: deposito === d ? "2px solid #0f172a" : "1.5px solid #e2e8f0",
                  background: deposito === d ? "#0f172a" : "#f8fafc",
                  color: deposito === d ? "#fff" : "#475569",
                  fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
                }}
              >
                {d}
              </button>
            ))}
          </div>

          <button
            onClick={cargarPedidos}
            disabled={loading}
            style={{
              width: "100%", padding: "12px", borderRadius: "10px",
              border: "none", background: "#3b82f6", color: "#fff",
              fontWeight: 700, fontSize: "0.95rem", cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Cargando…" : `Ver pedidos de ${deposito}`}
          </button>
        </div>

        {/* Lo que se conserva (info) */}
        {pedidos && (
          <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "14px", padding: "14px 20px", marginBottom: "16px" }}>
            <p style={{ margin: 0, fontSize: "0.88rem", color: "#166534" }}>
              ✅ Se conservan <strong>{conservados.length} pedido{conservados.length !== 1 ? "s" : ""} CONTROLADO</strong> (pendientes de despachar)
              {totalDespachados > 0 && <> · <strong>{totalDespachados} despachado{totalDespachados !== 1 ? "s" : ""}</strong> (no se tocan)</>}.
            </p>
          </div>
        )}

        {/* Resumen de lo que se elimina */}
        {pedidos && (
          <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "20px", marginBottom: "16px" }}>
            <p style={{ margin: "0 0 12px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              A eliminar en {deposito}: {aEliminar.length} pedido{aEliminar.length !== 1 ? "s" : ""}
            </p>

            {aEliminar.length === 0 ? (
              <div style={{ textAlign: "center", padding: "12px", background: "#f0fdf4", borderRadius: "10px" }}>
                <p style={{ margin: 0, color: "#15803d", fontWeight: 600 }}>
                  ✅ No hay pedidos sin controlar para eliminar
                </p>
              </div>
            ) : (
              <>
                {/* Breakdown por estado */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
                  {Object.entries(resumenEstados)
                    .sort(([, a], [, b]) => b - a)
                    .map(([estado, cant]) => (
                      <div key={estado} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: "8px", background: "#fef2f2", border: "1px solid #fecaca" }}>
                        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#b91c1c" }}>{estado}</span>
                        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#b91c1c" }}>{cant}</span>
                      </div>
                    ))}
                </div>

                <p style={{ margin: "0 0 12px", fontSize: "0.88rem", color: "#334155" }}>
                  Se van a <strong>eliminar definitivamente {aEliminar.length} pedido{aEliminar.length !== 1 ? "s" : ""}</strong> de <code>pedidos</code> (los que no llegaron a control).
                </p>

                {!confirmando ? (
                  <button
                    onClick={() => setConfirmando(true)}
                    style={{
                      width: "100%", padding: "13px", borderRadius: "10px",
                      border: "none", background: "#dc2626", color: "#fff",
                      fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
                    }}
                  >
                    Eliminar {aEliminar.length} pedido{aEliminar.length !== 1 ? "s" : ""} sin controlar
                  </button>
                ) : (
                  <div style={{ background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: "10px", padding: "14px" }}>
                    <p style={{ margin: "0 0 12px", fontSize: "0.88rem", fontWeight: 600, color: "#991b1b", textAlign: "center" }}>
                      ⚠️ ¿Confirmás? Se eliminan definitivamente. Esta acción no se puede deshacer.
                    </p>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        onClick={() => setConfirmando(false)}
                        style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 600, cursor: "pointer" }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={eliminar}
                        disabled={borrando}
                        style={{ flex: 2, padding: "10px", borderRadius: "8px", border: "none", background: "#dc2626", color: "#fff", fontWeight: 700, cursor: borrando ? "not-allowed" : "pointer", opacity: borrando ? 0.7 : 1 }}
                      >
                        {borrando ? "Eliminando…" : "Sí, eliminar"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Resultado */}
        {resultado && (
          <div style={{
            background: resultado.ok ? "#f0fdf4" : "#fef2f2",
            border: `1.5px solid ${resultado.ok ? "#86efac" : "#fca5a5"}`,
            borderRadius: "14px", padding: "20px", textAlign: "center",
          }}>
            {resultado.ok ? (
              <>
                <p style={{ margin: "0 0 4px", fontSize: "1.5rem" }}>✅</p>
                <p style={{ margin: 0, fontWeight: 700, color: "#15803d" }}>
                  {resultado.countDeleted} pedido{resultado.countDeleted !== 1 ? "s" : ""} eliminado{resultado.countDeleted !== 1 ? "s" : ""}
                </p>
                <p style={{ margin: "6px 0 0", fontSize: "0.85rem", color: "#15803d" }}>
                  Se conservaron {resultado.countKept} CONTROLADO (pendientes de despachar).
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: "0 0 4px", fontSize: "1.5rem" }}>❌</p>
                <p style={{ margin: 0, fontWeight: 700, color: "#dc2626" }}>Error: {resultado.error}</p>
              </>
            )}
          </div>
        )}

        <p style={{ textAlign: "center", marginTop: "24px", fontSize: "0.75rem", color: "#94a3b8" }}>
          Usuario: {user?.email || "—"} · <a href="/inicio" style={{ color: "#94a3b8" }}>Volver al inicio</a>
        </p>
      </div>
    </div>
  );
}
